import { apiUrl } from './api_config'

const API_BASE_URL = apiUrl('/customers')
const GET_ALL_CUSTOMER_ENDPOINT = `${API_BASE_URL}/all`
const GET_CUSTOMER_LIST_ENDPOINT = `${API_BASE_URL}/list`
const GET_CUSTOMER_EXPORT_ENDPOINT = `${API_BASE_URL}/export`
const GET_CUSTOMER_ENDPOINT = `${API_BASE_URL}/getCustomer`
const SAVE_CUSTOMER_ENDPOINT = `${API_BASE_URL}/add`
const UPDATE_CUSTOMER_ENDPOINT = `${API_BASE_URL}/updateCustomer`
const DELETE_CUSTOMER_ENDPOINT = `${API_BASE_URL}/deleteCustomer`
const REGIONS_ENDPOINT = `${API_BASE_URL}/regions`
const SALES_EXECUTIVES_ENDPOINT = `${API_BASE_URL}/sales-executives`
const CUSTOMER_TYPES_ENDPOINT = `${API_BASE_URL}/customer-types`

async function request(url, options = {}) {
  const response = await fetch(url, {
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
  })

  if (!response.ok) {
    let message = `Customer request failed (${response.status})`
    try {
      const error = await response.json()
      message = error.message || error.error || message
    } catch {
      // Keep the HTTP status message when the server does not return JSON.
    }
    throw new Error(message)
  }

  if (response.status === 204) return null
  return response.json()
}

function findCustomerList(data) {
  if (Array.isArray(data)) return data
  if (!data || typeof data !== 'object') return []

  for (const key of ['customers', 'customerList', 'results', 'items', 'data']) {
    const list = findCustomerList(data[key])
    if (list.length || Array.isArray(data[key])) return list
  }

  return []
}

function findNextCursor(data) {
  if (!data || typeof data !== 'object') return null
  return (
    data.nextCursor ??
    data.next_cursor ??
    data.nextPageToken ??
    data.next_page_token ??
    data.next ??
    findNextCursor(data.customers) ??
    findNextCursor(data.customerList) ??
    findNextCursor(data.results) ??
    findNextCursor(data.items) ??
    findNextCursor(data.data) ??
    findNextCursor(data.pagination) ??
    findNextCursor(data.meta) ??
    null
  )
}

export async function getCustomers({
  cursor = null,
  page = 1,
  search = '',
  signal,
} = {}) {
  const params = new URLSearchParams({ limit: '20' })
  if (cursor) params.set('cursor', cursor)
  else params.set('page', String(page))
  if (search.trim()) {
    params.set('search', search.trim())
  }
  const data = await request(
    `${GET_ALL_CUSTOMER_ENDPOINT}?${params.toString()}`,
    { signal },
  )
  const payload = data?.data && !Array.isArray(data.data) ? data.data : data
  const customers = findCustomerList(data)
  const nextCursor = findNextCursor(data)
  const currentPage = Number(
    payload?.page ??
      payload?.currentPage ??
      payload?.current_page ??
      data?.page ??
      data?.currentPage ??
      data?.current_page ??
      page,
  )
  const totalPages = Number(
    payload?.totalPages ??
      payload?.total_pages ??
      data?.totalPages ??
      data?.total_pages ??
      0,
  )
  const hasNext = Boolean(
    payload?.hasNext ??
    payload?.has_next ??
    data?.hasNext ??
    data?.has_next ??
    nextCursor ??
    (totalPages > currentPage ? true : false),
  )
  return {
    customers,
    nextCursor,
    hasNext,
    currentPage,
    nextPage: hasNext && !nextCursor ? currentPage + 1 : null,
    totalPerDay:
      payload?.custPerDay ?? data?.custPerDay ?? payload?.totalPerDay ?? null,
    totalPerWeek:
      payload?.custPerWeek ??
      data?.custPerWeek ??
      payload?.totalPerWeek ??
      null,
    totalPerMonth:
      payload?.custPerMonth ??
      data?.custPerMonth ??
      payload?.totalPerMonth ??
      null,
    totalPerYear:
      payload?.custPerYear ??
      data?.custPerYear ??
      payload?.totalPerYear ??
      null,
  }
}

export async function getAllCustomers(customerType = '') {
  const params = new URLSearchParams()
  if (
    customerType !== undefined &&
    customerType !== null &&
    customerType !== ''
  ) {
    params.set('cust_type', String(customerType))
  }

  const query = params.toString()
  return getList(
    await request(`${GET_CUSTOMER_LIST_ENDPOINT}${query ? `?${query}` : ''}`),
    'customers',
  )
}

export async function getCustomersForExport() {
  const data = await request(GET_CUSTOMER_EXPORT_ENDPOINT)
  return getList(data, 'customers')
}

export async function getCustomer(id) {
  const data = await request(
    `${GET_CUSTOMER_ENDPOINT}/${encodeURIComponent(id)}`,
  )
  const response = data?.data && !Array.isArray(data.data) ? data.data : data
  const wrappedPayload = response?.orderSummary
  const payload =
    wrappedPayload &&
    typeof wrappedPayload === 'object' &&
    (wrappedPayload.customer ||
      wrappedPayload.customerData ||
      wrappedPayload.orderSummary ||
      wrappedPayload.customerOrders ||
      wrappedPayload.customerPayments)
      ? wrappedPayload
      : response
  const customer = payload?.customer || payload?.customerData || payload || {}
  const orderSummary =
    payload?.orderSummary ||
    (payload === response
      ? response?.orderSummary
      : payload?.totalOrders !== undefined ||
          payload?.totalGross !== undefined ||
          payload?.totalDiscount !== undefined ||
          payload?.totalNet !== undefined ||
          payload?.totalPaid !== undefined ||
          payload?.totalBalance !== undefined
        ? payload
        : {}) ||
    {}
  const customerOrders = Array.isArray(payload?.customerOrders)
    ? payload.customerOrders
    : []
  const customerPayments = Array.isArray(payload?.customerPayments)
    ? payload.customerPayments
    : []

  return {
    ...customer,
    orderSummary,
    summary: orderSummary,
    customerOrders,
    customerPayments,
    orders: customerOrders,
    payments: customerPayments,
  }
}

function getList(data, key) {
  if (Array.isArray(data)) return data
  return data?.[key] || data?.data || []
}

export async function getRegions() {
  return getList(await request(REGIONS_ENDPOINT), 'regions')
}

export async function getSalesExecutives() {
  return getList(await request(SALES_EXECUTIVES_ENDPOINT), 'salesExecutives')
}

export async function getCustomerTypes() {
  return getList(await request(CUSTOMER_TYPES_ENDPOINT), 'customerTypes')
}

export async function createCustomer(customer) {
  const data = await request(SAVE_CUSTOMER_ENDPOINT, {
    method: 'POST',
    body: JSON.stringify(customer),
  })
  return data.customer || data.data || data
}

export async function updateCustomer(id, customer) {
  const data = await request(`${UPDATE_CUSTOMER_ENDPOINT}/${id}`, {
    method: 'PUT',
    body: JSON.stringify(customer),
  })
  return data.customer || data.data || data
}

export async function deleteCustomer(id) {
  await request(`${DELETE_CUSTOMER_ENDPOINT}/${id}`, { method: 'DELETE' })
}
